import './feedback.scss';

import { type CSSProperties, useEffect, useRef, useState } from 'react';

import { getUserData } from '../../constants/global';
import { useGlobalContext } from '../../context/global';
import {
	FEEDBACK_PRIORITY_LABELS,
	FEEDBACK_TYPE_COLORS,
	FEEDBACK_TYPE_DESCRIPTIONS,
	FEEDBACK_TYPE_LABELS,
	type FeedbackPriority,
	type FeedbackType,
} from '../../interface/feedback';
import { createFeedback, uploadFeedbackAttachment } from '../../lib/feedback-api';
import { TypeButton } from '../utils/button';
import { Hvc } from '../utils/hooks';
import { TypeInput } from '../utils/input';
import { BackArrowSVG, CameraSVG, CheckSVG, InfoSVG, PlusSVG, TrashSVG } from '../utils/svgs';
import { TypeTextArea } from '../utils/text-area';
import { getConsoleErrors } from './console-buffer';

type Step = 'type' | 'detail' | 'success';

interface IUploaded {
	id: number;
	name: string;
	previewUrl: string;
}

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 1000;
const PRIORITIES = Object.keys(FEEDBACK_PRIORITY_LABELS) as FeedbackPriority[];

const SubmitFeedback = () => {
	const { rsProps, actions } = useGlobalContext();

	const [step, setStep] = useState<Step>('type');
	const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [priority, setPriority] = useState<FeedbackPriority>('medium');
	const [attachments, setAttachments] = useState<IUploaded[]>([]);
	const [uploadingCount, setUploadingCount] = useState(0);
	const [submitting, setSubmitting] = useState(false);
	const [createdId, setCreatedId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const titleRef = useRef<HTMLInputElement>(null);
	const cameraRef = useRef<HTMLInputElement>(null);
	const browseRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (step === 'detail') {
			const t = setTimeout(() => titleRef.current?.focus(), 120);
			return () => clearTimeout(t);
		}
	}, [step]);

	const showPriority = selectedType === 'bug_report' || selectedType === 'error_report';
	const isValid = !!selectedType && title.trim().length > 0 && description.trim().length > 0;

	const handleSelectType = (type: FeedbackType) => {
		setSelectedType(type);
		setStep('detail');
	};

	const handleFiles = (fileList: FileList | null) => {
		if (!fileList?.length) return;
		Array.from(fileList).forEach((file) => {
			if (!file.type.startsWith('image/')) return;
			if (file.size > 10_000_000) {
				setError('Each image must be 10MB or less.');
				return;
			}
			const previewUrl = URL.createObjectURL(file);
			setUploadingCount((c) => c + 1);
			uploadFeedbackAttachment(file, 'image')
				.then((att) => {
					setAttachments((prev) => [...prev, { id: att.id, name: att.originalFilename || file.name, previewUrl }]);
				})
				.catch(() => {
					URL.revokeObjectURL(previewUrl);
					setError('Could not upload an image. Please try again.');
				})
				.finally(() => setUploadingCount((c) => Math.max(0, c - 1)));
		});
	};

	const removeAttachment = (id: number) => {
		setAttachments((prev) => {
			const target = prev.find((a) => a.id === id);
			if (target) URL.revokeObjectURL(target.previewUrl);
			return prev.filter((a) => a.id !== id);
		});
	};

	const resetForm = () => {
		attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
		setSelectedType(null);
		setTitle('');
		setDescription('');
		setPriority('medium');
		setAttachments([]);
		setError(null);
		setCreatedId(null);
		setStep('type');
	};

	const handleSubmit = async () => {
		if (!selectedType || !isValid || submitting) return;
		setSubmitting(true);
		setError(null);
		try {
			const user = getUserData().user;
			const result = await createFeedback({
				type: selectedType,
				title: title.trim(),
				description: description.trim(),
				priority: showPriority ? priority : undefined,
				pageUrl: window.location.href,
				browserInfo: navigator.userAgent,
				viewportSize: `${window.innerWidth}x${window.innerHeight}`,
				consoleErrors: getConsoleErrors(),
				attachmentIds: attachments.map((a) => a.id),
				metadata: {
					source: 'agent-frontend',
					partyId: user?.PartyId || null,
					// An observer has no party; their accreditation organisation is what triage needs.
					observerOrgId: Number(user?.ObserverId) || null,
					userRole: user?.role || null,
				},
			});
			setCreatedId(result.id);
			setStep('success');
			actions?.notificationGlobal('Thanks! Your feedback was submitted.', true);
		} catch {
			setError('We could not submit your feedback. Please check your connection and try again.');
		} finally {
			setSubmitting(false);
		}
	};

	const goToMyFeedback = () => {
		rsProps?.callSection({ action: 'view', component: 'feedback-mine', title: 'My Feedback' });
	};

	// Step 1 — choose a type
	if (step === 'type') {
		return (
			<div className='feedback-panel f-column-25'>
				<div className='f-row-7 align-items-center'>
					<InfoSVG
						width='16'
						height='16'
						color='var(--neutral-500)'
					/>
					<p className='m-0 text-little color-label'>What would you like to share?</p>
				</div>
				<div
					className='feedback-type-grid stagger-children'
					role='radiogroup'
					aria-label='Feedback type'
				>
					{(Object.keys(FEEDBACK_TYPE_LABELS) as FeedbackType[]).map((type) => (
						<div
							key={type}
							className='feedback-type-card'
							style={{ '--accent': FEEDBACK_TYPE_COLORS[type] } as CSSProperties}
							role='radio'
							aria-checked={selectedType === type}
							tabIndex={0}
							onClick={() => handleSelectType(type)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleSelectType(type);
								}
							}}
						>
							<span className='feedback-type-card__dot' />
							<p className='feedback-type-card__title m-0'>{FEEDBACK_TYPE_LABELS[type]}</p>
							<p className='feedback-type-card__desc m-0'>{FEEDBACK_TYPE_DESCRIPTIONS[type]}</p>
						</div>
					))}
				</div>
				<div className='feedback-panel__footer-link'>
					<button
						type='button'
						className='feedback-link-btn'
						onClick={goToMyFeedback}
					>
						View my feedback
					</button>
				</div>
			</div>
		);
	}

	// Step 3 — success
	if (step === 'success') {
		return (
			<div className='feedback-panel feedback-success f-column-25 align-items-center animate-fade-in'>
				<div className='feedback-success__icon'>
					<CheckSVG
						width='40'
						height='40'
						color='var(--success-500)'
					/>
				</div>
				<div className='f-column-7 align-items-center text-center'>
					<h3 className='m-0'>Feedback received</h3>
					<p className='m-0 text-little color-label'>
						Thank you for helping us improve the agent app.{createdId ? ` Reference #${createdId}.` : ''}
					</p>
				</div>
				<div className='f-column-12 w-100'>
					<TypeButton
						title='View my feedback'
						buttonSize='small'
						className='w-100'
						onClick={goToMyFeedback}
					/>
					<TypeButton
						title='Send more feedback'
						buttonType='outlined'
						buttonSize='small'
						className='w-100'
						onClick={resetForm}
					/>
				</div>
			</div>
		);
	}

	// Step 2 — details
	return (
		<div className='feedback-panel f-column-22'>
			<button
				type='button'
				className='feedback-back f-row-10 align-items-center'
				onClick={() => setStep('type')}
			>
				<BackArrowSVG />
				<span className='text-little'>Back</span>
			</button>

			{selectedType ? (
				<div
					className='feedback-type-pill'
					style={{ '--accent': FEEDBACK_TYPE_COLORS[selectedType] } as CSSProperties}
				>
					<span className='feedback-type-pill__dot' />
					{FEEDBACK_TYPE_LABELS[selectedType]}
				</div>
			) : null}

			<TypeInput
				ref={titleRef}
				label='Title'
				placeholder='Briefly summarise your feedback'
				value={title}
				maxLength={MAX_TITLE}
				onChange={(e) => setTitle(e.target.value)}
			/>

			<div className='feedback-textarea-wrap'>
				<TypeTextArea
					label='Details'
					placeholder='Tell us what happened, what you expected, or your idea...'
					value={description}
					onChange={(e) => {
						if (e.target.value.length > MAX_DESCRIPTION) return;
						setDescription(e.target.value);
					}}
				/>
				<span className='feedback-char-count'>
					{description.length}/{MAX_DESCRIPTION}
				</span>
			</div>

			<Hvc
				removeDOM
				view={showPriority}
				className='f-column-12'
			>
				<p className='m-0 text-little color-label'>How urgent is this?</p>
				<div
					className='feedback-priority-row'
					role='radiogroup'
					aria-label='Priority'
				>
					{PRIORITIES.map((p) => (
						<button
							key={p}
							type='button'
							role='radio'
							aria-checked={priority === p}
							className={`feedback-priority-pill ${priority === p ? 'selected' : ''} feedback-priority-pill--${p}`}
							onClick={() => setPriority(p)}
						>
							{FEEDBACK_PRIORITY_LABELS[p]}
						</button>
					))}
				</div>
			</Hvc>

			<div className='f-column-12'>
				<p className='m-0 text-little color-label'>Attach a photo (optional)</p>
				<div className='feedback-attach-row'>
					<button
						type='button'
						className='feedback-attach-btn'
						onClick={() => cameraRef.current?.click()}
					>
						<CameraSVG
							width='16'
							height='16'
							color='var(--primary-700)'
						/>
						<span>Camera</span>
					</button>
					<button
						type='button'
						className='feedback-attach-btn'
						onClick={() => browseRef.current?.click()}
					>
						<PlusSVG color='var(--primary-700)' />
						<span>Upload</span>
					</button>
					{uploadingCount > 0 ? <span className='feedback-attach-status text-little color-label'>Uploading…</span> : null}
				</div>
				<input
					ref={cameraRef}
					type='file'
					accept='image/*'
					capture='environment'
					className='d-none'
					onChange={(e) => {
						handleFiles(e.target.files);
						e.target.value = '';
					}}
				/>
				<input
					ref={browseRef}
					type='file'
					accept='image/*'
					multiple
					className='d-none'
					onChange={(e) => {
						handleFiles(e.target.files);
						e.target.value = '';
					}}
				/>

				<Hvc
					removeDOM
					view={attachments.length > 0}
					className='feedback-thumbs'
				>
					{attachments.map((att) => (
						<div
							key={att.id}
							className='feedback-thumb'
						>
							<img
								src={att.previewUrl}
								alt={att.name}
							/>
							<button
								type='button'
								className='feedback-thumb__remove'
								aria-label={`Remove ${att.name}`}
								onClick={() => removeAttachment(att.id)}
							>
								<TrashSVG color='#fff' />
							</button>
						</div>
					))}
				</Hvc>
			</div>

			<Hvc
				removeDOM
				view={!!error}
			>
				<p className='m-0 text-little color-danger'>{error}</p>
			</Hvc>

			<TypeButton
				title='Submit feedback'
				buttonSize='small'
				className='w-100'
				buttonType={!isValid ? 'disabled' : undefined}
				load={submitting}
				onClick={handleSubmit}
			/>
		</div>
	);
};

export default SubmitFeedback;
